import { PlacementEntityType, PlacementFile, PlacementSheet } from "./types";
import { EntityItem } from "@adsk/docs-js-sdk";
import { FILE_EXTENSION_MAPPING } from "../../utils/data/fileExtensionMapping";
import { getFileExtension } from "../../utils/data/getFileExtension";
import DoctypeIcon from "@adsk/nirvana-doctype-icon";
import { IconButton } from "@adsk/alloy-react-button";
import Theme from "@adsk/alloy-react-theme";
import {
  SheetsIcon,
  AlertWarningFilledIcon,
  XIcon,
} from "@adsk/alloy-react-icon";
import { Sheet } from "@adsk/acc-issues-core/sheets-service/SheetsApi";
import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { PlacementDeleteModal } from "../../IssueDetailPanel/IssueDetailTabPanel/detail-panel-fields/PlacementDeleteModal";

const EntityDisplayContainer = styled.div<{ error?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid ${Theme.colors.charcoal400};
  border-radius: 2px;
  background-color: ${props =>
    !!props.error ? Theme.colors.red300 : "transparent"};
`;

const DisplayNameContainer = styled.div`
  flex-grow: 1;
`;

const EntityIconAndName = ({
  entityType,
  entity,
}: {
  entityType: PlacementEntityType;
  entity: PlacementSheet | PlacementFile;
}) => {
  switch (entityType) {
    case PlacementEntityType.FILES: {
      const fileName = (entity as unknown as EntityItem).fileName;
      const fileTypeIcon = FILE_EXTENSION_MAPPING[getFileExtension(fileName!)];
      return (
        <>
          <span style={{ paddingRight: 8 }}>
            <DoctypeIcon type={fileTypeIcon} size={22} />
          </span>
          <DisplayNameContainer>{fileName}</DisplayNameContainer>
        </>
      );
    }
    case PlacementEntityType.SHEETS: {
      return (
        <>
          <span style={{ paddingRight: 8, marginTop: 2 }}>
            <SheetsIcon size={22} />
          </span>
          <DisplayNameContainer>{entity.name}</DisplayNameContainer>
        </>
      );
    }
    default:
      return null;
  }
};
export const EntityDisplay = ({
  entityType,
  entity,
  onRemove,
  error,
}: {
  entityType?: PlacementEntityType;
  entity?: Sheet | EntityItem;
  onRemove?: () => void;
  error?: boolean;
}) => {
  const [isPlacementDeleteModalOpen, setIsPlacementDeleteModalOpen] =
    useState(false);
  const handleButtonClick = useCallback(() => {
    setIsPlacementDeleteModalOpen(true);
  }, [setIsPlacementDeleteModalOpen]);
  const handleModalClose = useCallback(() => {
    setIsPlacementDeleteModalOpen(false);
  }, [setIsPlacementDeleteModalOpen]);
  if (!entityType || !entity) {
    return null;
  }
  const isFile = entityType == PlacementEntityType.FILES;
  const isSheet = entityType == PlacementEntityType.SHEETS;
  const placementText = isFile
    ? (entity as unknown as EntityItem).fileName
    : isSheet
    ? entity.name
    : undefined;
  return (
    <EntityDisplayContainer error={error}>
      <EntityIconAndName entityType={entityType} entity={entity} />
      <div style={{ display: "flex", alignItems: "center" }}>
        {error && (
          <div style={{ marginRight: "16px", marginTop: "4px" }}>
            <AlertWarningFilledIcon color={Theme.colors.red500} size={20} />
          </div>
        )}
        <IconButton
          renderIcon={p => <XIcon {...p} />}
          type="button"
          size={24}
          onClick={handleButtonClick}
        />
      </div>
      <PlacementDeleteModal
        isOpen={isPlacementDeleteModalOpen}
        placementText={placementText}
        isFile={isFile}
        onDelete={onRemove}
        onClose={handleModalClose}
      />
    </EntityDisplayContainer>
  );
};
